import { Card, CardContent } from "../ui/card";
import { Progress } from "../ui/progress";
import { Flame, Beef, Wheat, Droplet } from "lucide-react";

const ProgressBar = ({ label, value, goal, unit, colorFrom, colorTo, icon: Icon, testId }) => {
  const pct = goal > 0 ? Math.min(100, (value / goal) * 100) : 0;
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-gray-300">
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {label}
        </span>
        <span className="tabular-nums text-gray-600 dark:text-gray-400" data-testid={`${testId}-value`}>
          {Math.round(value)}<span className="text-gray-400">/{goal}{unit}</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${colorFrom}, ${colorTo})`,
          }}
        />
      </div>
    </div>
  );
};

export default function ProgressCards({ summary }) {
  if (!summary) return null;
  const goals = summary.goals || {};
  const totals = summary.totals || {};
  const burned = summary.exercise?.calories_burned || 0;
  const net = summary.net_calories || 0;
  const goalCal = goals.calories || 1;
  const remaining = summary.remaining_calories ?? goalCal - net;
  const netPct = Math.min(100, Math.max(0, (net / goalCal) * 100));

  return (
    <Card className="border-0 shadow-md" data-testid="nutrition-progress-card">
      <CardContent className="p-4 sm:p-5">
        {/* Calories headline */}
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Goal</div>
            <div className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white tabular-nums" data-testid="calorie-goal-value">
              {goalCal}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">Remaining</div>
            <div className={`text-2xl sm:text-3xl font-extrabold tabular-nums ${remaining < 0 ? "text-red-500" : "text-emerald-600"}`} data-testid="calories-remaining-value">
              {Math.round(remaining)}
            </div>
            <div className="text-[10px] text-gray-500">kcal</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Food − Exercise</div>
            <div className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white tabular-nums">
              {Math.round(totals.calories || 0)} − {Math.round(burned)}
            </div>
          </div>
        </div>

        {/* Calories bar */}
        <div className="space-y-1 mb-4">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 font-semibold text-gray-700 dark:text-gray-300">
              <Flame className="w-3.5 h-3.5 text-orange-500" /> Calories net
            </span>
            <span className="tabular-nums text-gray-600 dark:text-gray-400">
              {Math.round(net)}<span className="text-gray-400">/{goalCal} kcal</span>
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-orange-400 to-red-500 transition-all"
              style={{ width: `${netPct}%` }}
            />
          </div>
        </div>

        {/* Macros grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ProgressBar
            label="Protein"
            value={totals.protein_g || 0}
            goal={goals.protein_g || 0}
            unit="g"
            colorFrom="#10b981"
            colorTo="#059669"
            icon={Beef}
            testId="protein-goal-progress"
          />
          <ProgressBar
            label="Carbs"
            value={totals.carbs_g || 0}
            goal={goals.carbs_g || 0}
            unit="g"
            colorFrom="#3b82f6"
            colorTo="#2563eb"
            icon={Wheat}
            testId="carbs-goal-progress"
          />
          <ProgressBar
            label="Fats"
            value={totals.fats_g || 0}
            goal={goals.fats_g || 0}
            unit="g"
            colorFrom="#a855f7"
            colorTo="#7c3aed"
            icon={Droplet}
            testId="fats-goal-progress"
          />
        </div>
      </CardContent>
    </Card>
  );
}
